import { DataSource } from '@angular/cdk/collections';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepicker, MatDialog, MatDialogRef, MatPaginator, MatSelectChange, MatSnackBar, MatSort } from '@angular/material';
import * as moment from 'moment';
import { Observable } from 'rxjs/Rx';
import { fuseAnimations } from '../../../../core/animations';
import { FuseConfirmDialogComponent } from '../../../../core/components/confirm-dialog/confirm-dialog.component';
import { OrderAdminListQuery } from '../../../../core/services/orders/order.models';
import { ORDER_STATUS } from '../../../../core/services/orders/orders.constant';
import { UsersService } from '../../../../core/services/users/users.service';
import { VoutchersService } from '../../../../core/services/vouchers/vouchers.service';
import { CategoriesService } from '../categories/categories.service';
import { DashboardPreordersService } from './preorders.service';

@Component({
  selector: 'fuse-preorders',
  templateUrl: './preorders.component.html',
  styleUrls: ['./preorders.component.scss'],
  animations : fuseAnimations
})
export class PreordersComponent implements OnInit, AfterViewInit {

    operator: string;
    statuses = [
        ORDER_STATUS.PREORDER_RESERVED,
        ORDER_STATUS.PREORDER_CONFIRMED,
        ORDER_STATUS.PREORDER_AWAITING_PAYMENT,
        ORDER_STATUS.PREORDER_PAID,
        ORDER_STATUS.PREORDER_CANCELED,
        ORDER_STATUS.PREORDER_CANCELEDBYUSER,
    ];
    dataSource: FilesDataSource | null;
    displayedColumns = ["boarded", 'invoiceNum', 'type'/*, 'direction'*/, 'user', 'phone', 'quantity', 'amount', 'items', 'buttons'];

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('filter') filter: ElementRef;
    @ViewChild('startDatePicker') startDatePicker: MatDatepicker<any>;
    @ViewChild('endDatePicker') endDatePicker: MatDatepicker<any>;
    @ViewChild('from') fromDate: ElementRef;
    @ViewChild('to') toDate: ElementRef;
    @ViewChild(MatSort) sort: MatSort;

    selectedDates: any;
    inputFrom: FormControl;
    inputTo: FormControl;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;


    constructor(
        public readonly categoriesService: CategoriesService,
        public readonly ordersService: DashboardPreordersService,
        public snackBar: MatSnackBar,
        public dialog: MatDialog,
        readonly usersService: UsersService,
        public readonly vouchersService: VoutchersService,
    ) {
        this.selectedDates = {
          from: moment().startOf('month'),
          to: moment().endOf('month'),
        };
        this.inputFrom = new FormControl(this.from);
        this.inputTo = new FormControl(this.to);
    }

    set status(status: string)
    {
        localStorage.setItem('preorder_status', status);
    }
    get status() {
        return localStorage.getItem('preorder_status');
    }

    set category(category: string) {
        localStorage.setItem('preorder_category', category);
    }
    get category() {
        return localStorage.getItem('preorder_category');
    }

//#region Dates
    toUTCSmallDate(date?: any) {
        if (!date || typeof date === 'undefined') {
            date = new Date();
        }
        if (date instanceof moment) {
            date = (date as moment.Moment).toDate();
        }
        if (typeof date === 'string') {
            date = new Date(date);
        }
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
    }

    set from(from: Date)
    {
        if (!from) {
            localStorage.removeItem('preorder_from');
            return;
        }
        localStorage.setItem('preorder_from', from.toISOString());
    }
    get from() {
        const val = localStorage.getItem('preorder_from');
        if (val) {
            return this.toUTCSmallDate(val);
        }
        return this.toUTCSmallDate(this.selectedDates.from);
    }
    set to(to: Date)
    {
        if (!to) {
            localStorage.removeItem('preorder_to');
            return;
        }
        localStorage.setItem('preorder_to', to.toISOString());
    }
    get to() {
        const val = localStorage.getItem('preorder_to');
        if (val) {
            return this.toUTCSmallDate(val);
        }
        return this.toUTCSmallDate(this.selectedDates.to);
    }

    changeFrom(event: any) {
        if (event) {
            const date = this.toUTCSmallDate(event);
            this.from = date;
            this.refresh();
        }
    }

    changeTo(event: any) {
        if (event) {
            const date = this.toUTCSmallDate(event);
            this.to = date;
            this.refresh();
        }
    }
//#endregion Dates

    ngOnInit() {
        if (!this.status) {
            this.status = ORDER_STATUS.PREORDER_AWAITING_PAYMENT;
        }

        this.ordersService.page = this.getStoredNumber('preordersPageNumber', 0);
        this.ordersService.limit = this.getStoredNumber('preordersPageLength', 10);
        this.dataSource = new FilesDataSource(this.ordersService);

        this.usersService.list();
        this.categoriesService.getCategories();
    }

    ngAfterViewInit() {
        Observable.fromEvent(this.filter.nativeElement, 'keyup')
                  .debounceTime(150)
                  .distinctUntilChanged()
                  .subscribe(() => {
                      this.paginator.pageIndex = 0;
                      this.storePaginatorState(0, this.paginator.pageSize);
                      this.loadOrders({
                          page: 0,
                          limit: this.paginator.pageSize,
                          search: this.getCurrentSearch(),
                      });
                  });

        this.paginator.page.subscribe(() => {
            this.storePaginatorState(this.paginator.pageIndex, this.paginator.pageSize);
            this.loadOrders({
                page: this.paginator.pageIndex,
                limit: this.paginator.pageSize,
            });
        });

        this.sort.sortChange.subscribe(() => {
            this.paginator.pageIndex = 0;
            this.storePaginatorState(0, this.paginator.pageSize);
            this.loadOrders({
                page: 0,
                limit: this.paginator.pageSize,
                sortBy: this.sort.direction ? this.sort.active : '',
                sortDirection: this.sort.direction || '',
            });
        });

        this.loadOrders();
    }

    storePaginatorState(page: number = this.ordersService.page, limit: number = this.ordersService.limit) {
        localStorage.setItem('preordersPageNumber', page.toString());
        localStorage.setItem('preordersPageLength', limit.toString());
    }

    refresh() {
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.storePaginatorState(0, this.ordersService.limit);
        this.loadOrders({
            page: 0,
            limit: this.ordersService.limit,
        });
    }

    reset() {
        this.category = '';
        this.operator = null;
        this.status = ORDER_STATUS.PREORDER_AWAITING_PAYMENT;
        this.startDatePicker._select(null);
        this.endDatePicker._select(null);
        this.fromDate.nativeElement.value = null;
        this.toDate.nativeElement.value = null;
        this.from = null;
        this.to = null;
        this.refresh();
    }

    selectChange(event: MatSelectChange)
    {
        // this.status = event.value;
        this.refresh();
    }

    operatorChange(event: MatSelectChange) {
        this.refresh();
    }

    confirmPreOrder(id) {
        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            disableClose: false
        });

        this.confirmDialogRef.componentInstance.confirmMessage = `Souhaitez-vous confirmer l'option de réservation réf. ${id}`;

        this.confirmDialogRef.afterClosed().subscribe(result => {
            if ( result )
            {
                this.ordersService.setStatus(id, ORDER_STATUS.PREORDER_CONFIRMED).then(() => {
                    this.snackBar.open(`Réservation ${id} confirmée avec succès.`, 'OK', {
                        verticalPosition: 'top',
                        duration        : 7000
                    });
                    this.loadOrders();
                }).catch(e => {
                    this.snackBar.open(`Confirmation impossible, une erreur est survenue.`, 'OK', {
                        verticalPosition: 'top',
                        duration        : 9000
                    });
                });
            }
            this.confirmDialogRef = null;
        });
    }

    deletePreOrder(id: string) {
        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            disableClose: false
        });

        this.confirmDialogRef.componentInstance.confirmMessage = `Veuillez confirmer l'annulation de le réservation réf.${id}`;

        this.confirmDialogRef.afterClosed().subscribe(result => {
            if ( result )
            {
                this.ordersService.setStatus(id, ORDER_STATUS.PREORDER_CANCELED).then(() => {
                    this.snackBar.open(`Réservation ${id} annulée.`, 'OK', {
                        verticalPosition: 'top',
                        duration        : 3000
                    });
                    this.loadOrders();
                }).catch(e => {
                    this.snackBar.open(`Annulation impossible, une erreur est survenue.`, 'OK', {
                        verticalPosition: 'top',
                        duration        : 3000
                    });
                });
            }
            this.confirmDialogRef = null;
        });
    }

    hasBoardedItems(order) {
        return order.items.some((item) => item.boarded > 0);
    }

    private getStoredNumber(key: string, fallback: number) {
        const value = Number.parseInt(localStorage.getItem(key), 10);
        return Number.isNaN(value) ? fallback : value;
    }

    private getCurrentSearch() {
        return this.filter && this.filter.nativeElement
            ? this.filter.nativeElement.value || ''
            : '';
    }

    private buildQuery(overrides: OrderAdminListQuery = {}): OrderAdminListQuery {
        return {
            status: this.status,
            from: this.from,
            to: this.to,
            operator: this.operator,
            category: this.category,
            page: this.ordersService.page,
            limit: this.ordersService.limit,
            search: this.getCurrentSearch(),
            sortBy:
                this.sort && this.sort.direction
                    ? this.sort.active
                    : this.ordersService.sortBy || '',
            sortDirection:
                this.sort && this.sort.direction
                    ? this.sort.direction
                    : this.ordersService.sortDirection || '',
            ...overrides,
        };
    }

    private loadOrders(overrides: OrderAdminListQuery = {}) {
        return this.ordersService.loadOrders(this.buildQuery(overrides));
    }

}

export class FilesDataSource extends DataSource<any> {
    constructor(private productsService: DashboardPreordersService)
    {
        super();
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<any[]>
    {
        return this.productsService.onOrdersChanged;
    }

    disconnect()
    {
    }
}



// WEBPACK FOOTER //
// /usr/app/src/app/main/content/app/preorders/preorders.component.ts